import {
  buildComplianceLoadingHTML,
  buildComplianceModalHTML,
  compGradeColor,
  ensureComplianceSpinnerStyle
} from './complianceModal.ts';

const DEFAULT_DOB = '05/29/2023';

/** Main Functions */
export function getPageInfo() {
  /*
    Description: This function will get the page info from the note
    Return: A dictionary with the page info
        {
            "URL": Base URL for the page,
            "Note Type": "PT Daily/SOAP Note",...
        }
    */
  // The Clinicient sandbox lives at test2-insight.clinicient.com but uses the
  // same templates as production, which are keyed on insight.clinicient.com.
  // Normalize so sandbox sessions resolve the same template set.
  const baseURL = window.location.origin.replace(
    'https://test2-insight.clinicient.com',
    'https://insight.clinicient.com'
  );

  let noteTypeElement = document.querySelector('footer.ng-star-inserted');
  let noteType = noteTypeElement ? noteTypeElement.textContent.trim() : null;
  if (noteType) {
    noteType = noteType.replace('– ', '').trim(); // Replace a leading "– " if it exists
  } else {
    noteType = 'Default Note';
  }

  const tenantName = document.querySelector('.database-name')?.textContent?.trim();

  return {
    URL: baseURL,
    'Note Type': noteType,
    ...(tenantName && { Metadata: { tenant_name: tenantName } })
  };
}

export function getPageInfoForCapture() {
  return getPageInfo();
}

export function getPatientInfo() {
  /*
    Description: This function will get the patient info from the note
    Return: A dictionary with the patient info
    {
        "EMR ID": 0x1234 or "michalskij"
        "First Name":firstName,
        "Last Name": lastName,
        "DOB": dob,
    }
    */
  // use the query selector to get the patient info
  const legalNameElement = document.querySelector('.legal-name');
  if (!legalNameElement) {
    return null;
  }

  // Extract the text inside the parentheses and split by ','
  const legalNameText = legalNameElement.textContent.trim();
  const nameMatch = legalNameText.match(/\(Legal:\s*([^,]+),\s*([^\)]+)\)/);
  let firstName, lastName;
  if (nameMatch && nameMatch.length === 3) {
    firstName = nameMatch[2].trim();
    lastName = nameMatch[1].trim();
    console.log(`First Name: ${firstName}, Last Name: ${lastName}`);
  } else {
    alert('Unable to extract the legal name correctly.');
  }

  const emrIDMatch = window.location.href.match(/patients\/(\d+)/);
  let emrID;
  if (emrIDMatch && emrIDMatch[1]) {
    emrID = emrIDMatch[1];
    console.log(`EMR ID: ${emrID}`);
  } else {
    alert('Unable to extract EMR ID from the URL');
  }
  const dob = getDOB();

  return {
    'EMR ID': emrID,
    'First Name': firstName,
    'Last Name': lastName,
    DOB: dob
  };
}

// Capture must quietly wait for patient data instead of showing panel alerts.
export function getPatientInfoForCapture() {
  const legalNameText = document
    .querySelector('.legal-name')
    ?.textContent?.trim();
  if (!legalNameText) return null;

  const nameMatch = legalNameText.match(/\(Legal:\s*([^,]+),\s*([^)]+)\)/);
  const emrID = getPatientKeyFromUrl();
  if (!nameMatch || !emrID) return null;

  return {
    'EMR ID': emrID,
    'First Name': nameMatch[2].trim(),
    'Last Name': nameMatch[1].trim(),
    DOB: getDOB()
  };
}

function getDOB() {
  // Step 1: Select the element containing the date
  // Assuming the structure of the HTML remains consistent
  let birthDateElement = document.querySelector(
    '.section.text-selectable.sensitive span.text-muted'
  );

  if (!birthDateElement) {
    console.error('Birth date element not found');
    return DEFAULT_DOB;
  }
  birthDateElement = birthDateElement.nextSibling;

  // Step 2: Extract the text content
  let birthDate = birthDateElement.textContent.trim();
  return birthDate;
}

/* 
questions - This is where we will store the questions
          - a list of dictionaries
     [{
        "Question": "Where was the injury?", 
        "Type": "Multiple Choice", 
        "Choices": ["Back", "Neck", "Shoulder"]
     },
        ...
        ]

*/
const panelQuestionsState = createQuestionsState(false);
const captureQuestionsState = createQuestionsState(true);

/*
question Interactions - This is where we will store the interactions for each question
                        - a dictionary of dictionaries
         {
            "Where was the injury?": {
                "Type": "Multiple Choice",
                "Choices"{
                    "Back": () => {click the back button},
                    "Neck": () => {click the neck button},
                    "Shoulder": () => {click the shoulder button}
                }
            },
        },
        {
            "What is the injury?": {
                "Type": "Long Answer",
                "Input": (val) => {input the value into the input box}
        }
            ...
         }
*/
const questionInteractions = panelQuestionsState.questionInteractions;

function createQuestionsState(disableScrapeSideEffects) {
  return {
    questionInteractions: {},
    disableScrapeSideEffects
  };
}

export async function getQuestions() {
  return buildQuestions(panelQuestionsState);
}

/**
 * Return current note values keyed by question key. Summary values are strings;
 * Flex values preserve their nested section and field keys.
 *
 * { [questionKey]: string | { [fieldKey]: string | nested values } }
 */
export async function getQuestionsForCapture(): Promise<Record<string, any>> {
  const questions = await buildQuestions(captureQuestionsState);
  return extractCaptureValues(questions);
}

async function buildQuestions(state) {
  const questions = [];
  Object.keys(state.questionInteractions).forEach((key) => {
    delete state.questionInteractions[key];
  });

  await getFlexQuestions(state);

  // Select all mat-expansion-panel elements to capture top-level Summary fields
  // as standalone Long Answer questions (separate from the Flex Details structure).
  const panels = document.querySelectorAll('mat-expansion-panel');
  panels.forEach(function (panel) {
    const ariaLabel = panel.getAttribute('aria-label');

    // Extract values and notes for each chart item
    const chartItems = panel.querySelectorAll('fx-chart-item');

    Array.from(chartItems).forEach(function (item) {
      const valueField = item.querySelector(
        'fx-input-measure input[clntformcontrolname="value"]'
      );
      const notesField = item.querySelector(
        'clnt-input-textarea[clntformcontrolname="note"] textarea'
      );

      // Look for additional textarea if notesField and valueField are not found
      const extraTextarea = item.querySelector('textarea.mat-input-element');

      // Check if the chart item context menu exists
      const contextMenuItem = item.querySelector('.chart-item-contextmenu');
      let name = contextMenuItem ? contextMenuItem.innerText.trim() : ''; // Use an empty string if not found
      if (name === '') {
        name = 'Summary';
      }
      let sectionName = ariaLabel + ' - ' + name;

      // Only proceed if the question is 'Summary'
      // Also, don't proceed into the details area... the first summary is the only summary
      if (
        name.includes('Summary') &&
        !state.questionInteractions[sectionName]
      ) {
        // Create a question object
        state.questionInteractions[sectionName] = {
          Type: 'Long Answer', // Assuming all items are long answer types
          Question: sectionName,
          Key: sectionName,
          OriginalElement: item,
          ExistingNote: notesField
            ? notesField.value.trim()
            : valueField
              ? valueField.value.trim()
              : extraTextarea
                ? extraTextarea.value.trim()
                : '', // Check for notesField, fallback to valueField, then to extraTextarea, else empty
          Input: async (val, replyType) => {
            if (isEmptyOrNull(val)) {
              return;
            }
            const inputField = notesField || valueField || extraTextarea; // Use the first available field

            if (!inputField) {
              console.error('No input field available for', sectionName);
              return;
            }

            // Focus on the element
            inputField.focus();

            // Click the element
            inputField.click();

            if (replyType === 'append') {
              inputField.value = (inputField.value + '\n' + val).trim();
            } else {
              inputField.value = val;
            }

            // Create and dispatch input event
            const inputEvent = new Event('input', { bubbles: true });
            inputField.dispatchEvent(inputEvent);

            // Create and dispatch change event
            const changeEvent = new Event('change', { bubbles: true });
            inputField.dispatchEvent(changeEvent);

            // Blur the element
            inputField.blur();

            // Create and dispatch blur event
            const blurEvent = new Event('blur', { bubbles: true });
            inputField.dispatchEvent(blurEvent);

            // Trigger the 'change' event if needed
            var event = new Event('change', { bubbles: true });
            inputField.dispatchEvent(event);
          }
        };
        console.log('Question:', state.questionInteractions);
      }
    });
  });

  //Sort the questions based on Dom order
  // This list must be ordered based on where the OriginalElement (DOM element) is located in the document order
  Object.keys(state.questionInteractions)
    .sort((a, b) => {
      const elementA = state.questionInteractions[a].OriginalElement;
      const elementB = state.questionInteractions[b].OriginalElement;
      if (
        elementA.compareDocumentPosition(elementB) &
        Node.DOCUMENT_POSITION_FOLLOWING
      ) {
        return -1;
      } else {
        return 1;
      }
    })
    .forEach((key) => {
      const question = { ...state.questionInteractions[key] };
      delete question.OriginalElement;
      if (
        question.Type == 'Flex' &&
        question.Structure &&
        Object.keys(question.Structure).length == 0
      ) {
        return;
      }
      questions.push(question);
    });

  console.log('Sorted questions:', questions);
  questions.forEach((question) => {
    console.log(question.Key);
  });
  return questions;
}

function extractCaptureValues(questions) {
  const values = {};

  questions.forEach((question, index) => {
    const key = question?.Key || question?.Question || String(index);
    const value = getCaptureValue(question);
    if (value !== null && value !== undefined) {
      values[key] = value;
    }
  });

  return values;
}

function getCaptureValue(question) {
  if (question.ExistingNote !== null && question.ExistingNote !== undefined) {
    return question.ExistingNote;
  }
  if (
    question['Existing Value'] !== null &&
    question['Existing Value'] !== undefined
  ) {
    return question['Existing Value'];
  }
  if (question.Structure) {
    const values = extractFlexCaptureValues(question.Structure);
    return Object.keys(values).length > 0 ? values : null;
  }
  return null;
}

function extractFlexCaptureValues(structure) {
  const values = {};

  Object.entries(structure || {}).forEach(([key, value]) => {
    if (!value || typeof value !== 'object') return;

    if (
      value['Existing Value'] !== null &&
      value['Existing Value'] !== undefined
    ) {
      values[key] = value['Existing Value'];
      return;
    }

    const nested = extractFlexCaptureValues(value);
    if (Object.keys(nested).length > 0) {
      values[key] = nested;
    }
  });

  return values;
}

/**
 * Return a stable key for the open note. Each EMR may derive this differently,
 * but the same note must always return the same key and two notes must not
 * share one. Clinicient exposes patient and visit IDs in the URL.
 */
export function getNoteKeyForCapture() {
  const patientKey = getPatientKeyFromUrl();
  const visitKey = getVisitKeyFromUrl();
  if (!patientKey || !visitKey) return null;
  return `patient:${patientKey}|visit:${visitKey}`;
}

export const noteFinalizationSelectorForCapture =
  '[data-testid="sign-off-button"]';

function getPatientKeyFromUrl() {
  const emrIDMatch = window.location.href.match(/patients\/(\d+)/);
  return emrIDMatch?.[1] || null;
}

function getVisitKeyFromUrl() {
  const visitIDMatch = window.location.href.match(/visits\/(\d+)/);
  return visitIDMatch?.[1] || null;
}

function getFlexQuestions(state) {
  let structure = {};
  getTextQuestions(structure, state);
  getNumericQuestions(structure, state);
  getProcedureQuestions(structure, state);

  // add the structure to questionInteractions
  Object.keys(structure).forEach((key) => {
    state.questionInteractions[key + ' - Details'] = {
      Type: 'Flex',
      Key: key + ' - Details',
      Question: structure[key]?.Question + ' - Details',
      Structure: structure[key]?.Structure,
      OriginalElement: structure[key]?.OriginalElement,
      ReplyType: 'no_reply'
    };
  });
}

function getTextQuestions(structure, state) {
  const textareas = document.querySelectorAll('textarea');

  textareas.forEach((textarea) => {
    // Skip if in Procedures section (handled by getProcedureQuestions)
    const panel = textarea.closest('mat-expansion-panel');
    if (panel) {
      const ariaLabel = panel.getAttribute('aria-label');
      if (ariaLabel && ariaLabel.includes('Procedures')) {
        return;
      }
    }

    // Get section, base label, and sub-label
    let sectionLabel = getClosestSectionLabel(textarea);
    let baseLabel = getClosestLabel(textarea);
    let subBaseLabel = getSubBaseLabel(textarea);

    // Log warnings if key labels are missing
    if (!baseLabel) {
      console.warn(`Missing label for:`, textarea);
      baseLabel =
        subBaseLabel ||
        `Unknown Field (${textarea.id || textarea.name || 'No ID'})`;
    }

    if (!subBaseLabel) {
      console.warn(`Missing sub-label for:`, textarea);
      subBaseLabel = baseLabel; // Fallback if no sub-label found
    }

    // **Ensure the section exists before adding fields**
    if (!structure[sectionLabel]) {
      structure[sectionLabel] = {
        Question: sectionLabel,
        Structure: {},
        OriginalElement: textarea
      };
    }

    // Check if this is a value field (inside clnt-select-combo) or a notes field
    const selectCombo = textarea.closest('clnt-select-combo');
    const isValueField = selectCombo !== null;
    const isSummary =
      `${sectionLabel} - ${baseLabel} - ${subBaseLabel}`.endsWith('Summary');

    // Skip top-level plain textareas Summary that are already captured as standalone
    // Long Answer questions (e.g. "Subjective - Summary" Long Answer).
    // BUT keep value fields (clnt-select-combo) at the same level... even if they user the word summary...
    if (isSummary && !isValueField) {
      return;
    }

    if (!structure[sectionLabel].Structure[baseLabel]) {
      structure[sectionLabel].Structure[baseLabel] = {};
    }

    const isNotesField =
      textarea.closest('clnt-input-textarea[clntformcontrolname="note"]') !==
        null || textarea.getAttribute('clntformcontrolname') === 'note';

    // Determine the key suffix
    let keySuffix = '';
    if (isValueField) {
      // Value field - no suffix
      keySuffix = '';
    } else if (isNotesField) {
      // Notes field - add Notes suffix
      keySuffix = ' - Notes';
    } else {
      // Default to Notes suffix for backward compatibility
      keySuffix = ' - Notes';
    }

    const fieldKey = `${sectionLabel} - ${baseLabel} - ${subBaseLabel}${keySuffix}`;

    // Skip if this key already exists (to avoid duplicates)
    if (structure[sectionLabel].Structure[baseLabel][fieldKey]) {
      return;
    }

    // Extract choices from clnt-select-combo menu if available
    let choices: string[] = [];
    let description = '';
    if (isValueField && selectCombo) {
      // Try multiple ways to find menu items
      // 1. Check mat-menu directly in the component
      const menu = selectCombo.querySelector('mat-menu');
      if (menu) {
        const menuItems = menu.querySelectorAll('button[mat-menu-item]');
        menuItems.forEach((item) => {
          const ariaLabel = item.getAttribute('aria-label');
          if (ariaLabel) {
            choices.push(ariaLabel.trim());
          } else {
            const text = item.textContent?.trim();
            if (text) {
              choices.push(text);
            }
          }
        });
      }

      // 2. Check clnt-select-combo-menu component
      if (choices.length === 0) {
        const comboMenu = selectCombo.querySelector('clnt-select-combo-menu');
        if (comboMenu) {
          const menuItems = comboMenu.querySelectorAll('button[mat-menu-item]');
          menuItems.forEach((item) => {
            const ariaLabel = item.getAttribute('aria-label');
            if (ariaLabel) {
              choices.push(ariaLabel.trim());
            } else {
              const text = item.textContent?.trim();
              if (text) {
                choices.push(text);
              }
            }
          });
        }
      }

      // If choices found, create description
      if (choices.length > 0) {
        description = `Select all applicable answers. If none apply, reply with 'None'. Provide your response as a list of choices separated by a '$' symbol. The available options are: ${choices.join(', ')}`;
      }
    }

    const fieldEntry: any = {
      'Existing Value': textarea.value || '',
      Input: async (val: string, replyType: string) => {
        if (isEmptyOrNull(val)) {
          return;
        }
        const inputField = textarea;

        if (!inputField) {
          console.error(' No input field available for', subBaseLabel);
          return;
        }

        // Focus on the element
        inputField.focus();

        // Click the element
        inputField.click();

        // Modify input value based on replyType
        if (replyType === 'append') {
          inputField.value = (inputField.value + '\n' + val).trim();
        } else {
          inputField.value = val;
        }

        // Dispatch input and change events to update UI
        ['input', 'change', 'blur'].forEach((event) =>
          inputField.dispatchEvent(new Event(event, { bubbles: true }))
        );

        console.log(`Updated field: ${subBaseLabel} with value:`, val);
      }
    };

    // Add description if choices were found
    if (description) {
      fieldEntry.Description = description;
    }

    structure[sectionLabel].Structure[baseLabel][fieldKey] = fieldEntry;

    // Attach an event listener to update values dynamically
    addCaptureSafeInputListener(state, textarea, function () {
      if (structure[sectionLabel].Structure[baseLabel][fieldKey]) {
        structure[sectionLabel].Structure[baseLabel][fieldKey][
          'Existing Value'
        ] = textarea.value;
      }
    });
  });
  return structure;
}

function getNumericQuestions(structure, state) {
  const numericInputSelector =
    'input[digitonly]:not([hidden]), input[type="text"]:not([hidden])';
  // Only process inputs with digitonly attribute or inputs in chart items with measure labels
  const allInputs = document.querySelectorAll(numericInputSelector);
  const numericInputs: HTMLInputElement[] = [];

  allInputs.forEach((input) => {
    // Check if input has digitonly attribute
    if (input.hasAttribute('digitonly')) {
      numericInputs.push(input as HTMLInputElement);
    } else {
      // Check if input is in a chart item with a measure label
      const chartItem = input.closest('fx-chart-item');
      if (chartItem && chartItem.querySelector('label.measure-label')) {
        numericInputs.push(input as HTMLInputElement);
      }
    }
  });

  numericInputs.forEach((input) => {
    // Skip if in Procedures section (handled by getProcedureQuestions)
    const panel = input.closest('mat-expansion-panel');
    if (panel) {
      const ariaLabel = panel.getAttribute('aria-label');
      if (ariaLabel && ariaLabel.includes('Procedures')) {
        return;
      }
    }

    // Get section, base label, and sub-label
    let sectionLabel = getClosestSectionLabel(input);
    let baseLabel = getClosestLabel(input);
    let subBaseLabel = getSubBaseLabel(input);

    // Log warnings if key labels are missing
    if (!baseLabel) {
      console.warn(`Missing label for:`, input);
      baseLabel =
        subBaseLabel || `Unknown Field (${input.id || input.name || 'No ID'})`;
    }

    if (!subBaseLabel) {
      console.warn(`Missing sub-label for:`, input);
      subBaseLabel = baseLabel; // Fallback if no sub-label found
    }

    // **Ensure the section exists before adding fields**
    if (!structure[sectionLabel]) {
      structure[sectionLabel] = {
        Question: sectionLabel,
        Structure: {},
        OriginalElement: input
      };
    }

    if (
      `${sectionLabel} - ${baseLabel} - ${subBaseLabel}`.endsWith('Summary')
    ) {
      return;
    }

    if (!structure[sectionLabel].Structure[baseLabel]) {
      structure[sectionLabel].Structure[baseLabel] = {};
    }

    // Find the chart item container to get unit label and notes
    const chartItem = input.closest('fx-chart-item');
    //<div _ngcontent-swp-c674="" class="label-and-field ng-star-inserted"><fx-input-measure _ngcontent-swp-c674="" clntformcontrolname="value" class="em-w-4 f-lt-md-g-1" _nghost-swp-c672=""><clnt-input-text _ngcontent-swp-c672="" _nghost-swp-c433="" class="clnt-input-text"><mat-form-field _ngcontent-swp-c433="" class="mat-form-field ng-tns-c187-335 mat-primary mat-form-field-type-mat-input mat-form-field-appearance-legacy mat-form-field-can-float mat-form-field-should-float mat-form-field-hide-placeholder ng-untouched ng-pristine ng-valid ng-star-inserted"><div class="mat-form-field-wrapper ng-tns-c187-335"><div class="mat-form-field-flex ng-tns-c187-335"><!----><!----><div class="mat-form-field-infix ng-tns-c187-335"><input _ngcontent-swp-c433="" hidden="" class="ng-tns-c187-335"><input _ngcontent-swp-c433="" matinput="" class="mat-input-element mat-form-field-autofill-control clnt-form-control-input ng-tns-c187-335 ng-untouched ng-pristine ng-valid cdk-text-field-autofill-monitored" autocomplete="off" tabindex="0" type="text" id="mat-input-199" aria-invalid="false" aria-required="false"><span class="mat-form-field-label-wrapper ng-tns-c187-335"><!----></span></div><!----></div><div class="mat-form-field-underline ng-tns-c187-335 ng-star-inserted"><span class="mat-form-field-ripple ng-tns-c187-335"></span></div><!----><div class="mat-form-field-subscript-wrapper ng-tns-c187-335"><!----><div class="mat-form-field-hint-wrapper ng-tns-c187-335 ng-trigger ng-trigger-transitionMessages ng-star-inserted" style="opacity: 1; transform: translateY(0%);"><!----><div class="mat-form-field-hint-spacer ng-tns-c187-335"></div><!----></div><!----></div></div></mat-form-field><!----></clnt-input-text><!----></fx-input-measure><label _ngcontent-swp-c674="" class="em-w-4 left-align measure-label"> Measure </label></div>
    // The unit label is the label with class containing "after" near the input
    // Find the unit label (label with class containing "after" near the input)
    let unitLabel = '';
    if (chartItem) {
      const unitLabelElement = chartItem.querySelector('label.measure-label');
      if (unitLabelElement) {
        unitLabel = unitLabelElement.textContent.trim();
        // Remove leading "- " if present
        unitLabel = unitLabel.replace(/^-\s*/, '').trim();
      }
    }

    // Find the notes textarea in the same chart item
    let notesTextarea: HTMLTextAreaElement | null = null;
    if (chartItem) {
      const notesElement = chartItem.querySelector(
        'clnt-input-textarea[clntformcontrolname="note"] textarea, textarea[clntformcontrolname="note"]'
      );
      notesTextarea = notesElement as HTMLTextAreaElement | null;
    }

    // Resolve side per input using row-local Left/Right labels so each ROM value maps to the correct column.
    const sideLabel = getSideLabelForRowInput(input);

    // Include side in the key for bilateral ROM rows to prevent left/right key collisions.
    const valueKey = sideLabel
      ? unitLabel
        ? `${sectionLabel} - ${baseLabel} - ${subBaseLabel} - ${sideLabel} - ${unitLabel}`
        : `${sectionLabel} - ${baseLabel} - ${subBaseLabel} - ${sideLabel}`
      : unitLabel
        ? `${sectionLabel} - ${baseLabel} - ${subBaseLabel} - ${unitLabel}`
        : `${sectionLabel} - ${baseLabel} - ${subBaseLabel}`;

    structure[sectionLabel].Structure[baseLabel][valueKey] = {
      // For side-tagged fields, normalize values like "Left 20; Right 30" to side-specific text.
      'Existing Value': sideLabel
        ? getResolvedSideValueForRowInput(input, sideLabel)
        : input.value || '',
      Input: async (val: string, replyType: string) => {
        if (isEmptyOrNull(val)) {
          return;
        }
        const scopedVal = sideLabel
          ? getSideValue(val?.toString?.() ?? val, sideLabel)
          : val;
        const inputField = input;

        if (!inputField) {
          console.error(' No input field available for', subBaseLabel);
          return;
        }

        // Focus on the element
        inputField.focus();

        // Click the element
        inputField.click();

        // Modify input value based on replyType
        if (replyType === 'append') {
          inputField.value = (inputField.value + '\n' + scopedVal).trim();
        } else {
          inputField.value = scopedVal;
        }

        // Dispatch input and change events to update UI
        ['input', 'change', 'blur'].forEach((event) =>
          inputField.dispatchEvent(new Event(event, { bubbles: true }))
        );

        console.log(`Updated field: ${subBaseLabel} with value:`, val);
      }
    };

    // Attach an event listener to update values dynamically
    addCaptureSafeInputListener(state, input, function () {
      structure[sectionLabel].Structure[baseLabel][valueKey]['Existing Value'] =
        input.value;
    });

    // Create entry for notes if textarea exists
    if (notesTextarea) {
      const notesKey = `${sectionLabel} - ${baseLabel} - ${subBaseLabel} - Notes`;
      const notesField = notesTextarea; // Capture for use in closures

      // Only create notes entry if it doesn't already exist (to avoid duplicates)
      if (!structure[sectionLabel].Structure[baseLabel][notesKey]) {
        structure[sectionLabel].Structure[baseLabel][notesKey] = {
          'Existing Value': notesField.value || '',
          Input: async (val: string, replyType: string) => {
            if (isEmptyOrNull(val)) {
              return;
            }
            const inputField = notesField;

            if (!inputField) {
              console.error(' No notes field available for', baseLabel);
              return;
            }

            // Focus on the element
            inputField.focus();

            // Click the element
            inputField.click();

            // Modify input value based on replyType
            if (replyType === 'append') {
              inputField.value = (inputField.value + '\n' + val).trim();
            } else {
              inputField.value = val;
            }

            // Dispatch input and change events to update UI
            ['input', 'change', 'blur'].forEach((event) =>
              inputField.dispatchEvent(new Event(event, { bubbles: true }))
            );

            console.log(`Updated notes field: ${baseLabel} with value:`, val);
          }
        };

        // Attach an event listener to update notes values dynamically
        addCaptureSafeInputListener(state, notesField, function () {
          structure[sectionLabel].Structure[baseLabel][notesKey][
            'Existing Value'
          ] = notesField.value;
        });
      }
    }
  });
  return structure;
}

function getSideLabelForRowInput(input: HTMLInputElement) {
  const rowElement =
    input.closest('.chart-row-left-side') || input.closest('fx-chart-item');
  if (!rowElement) return null;

  const toSide = (text: string) => {
    const normalized = text.trim().toLowerCase();
    if (normalized === 'left') return 'Left Side';
    if (normalized === 'right') return 'Right Side';
    return null;
  };

  // Clinicient rows render side labels and side inputs in adjacent `.label-and-field` blocks.
  // Walk in DOM order, track the latest Left/Right label, and assign it to the matching input.
  const sideFields = rowElement.querySelectorAll('.label-and-field');
  let currentSide: 'Left Side' | 'Right Side' | null = null;
  for (const field of Array.from(sideFields)) {
    const directLabel = field.querySelector(':scope > label');
    const sideFromDirectLabel = directLabel
      ? toSide(directLabel.textContent || '')
      : null;
    if (sideFromDirectLabel) {
      currentSide = sideFromDirectLabel as 'Left Side' | 'Right Side';
    }

    if (field.contains(input)) {
      return currentSide;
    }
  }

  return null;
}

function getResolvedSideValueForRowInput(
  input: HTMLInputElement,
  sideLabel: 'Left Side' | 'Right Side' | string
) {
  const directValue = getSideValue(input.value || '', sideLabel);
  if (!isEmptyOrNull(directValue)) return directValue;

  const rowElement =
    input.closest('.chart-row-left-side') || input.closest('fx-chart-item');
  if (!rowElement) return directValue;

  // Some rows store both sides in one input and leave the other side empty.
  // If current side is empty, derive this side from sibling inputs within the same row only.
  const rowInputs = rowElement.querySelectorAll(
    'input[digitonly]:not([hidden]), input[type="text"]:not([hidden])'
  );
  for (const rowInput of Array.from(rowInputs)) {
    if (rowInput === input) continue;
    const candidateValue = (rowInput as HTMLInputElement).value || '';
    const extracted = getSideValue(candidateValue, sideLabel, false);
    if (!isEmptyOrNull(extracted)) return extracted;
  }

  return directValue;
}

function getSideValue(
  value: string,
  sideLabel: 'Left Side' | 'Right Side' | string,
  fallbackToRaw = true
) {
  if (!value || typeof value !== 'string') return value;

  const text = value.trim();
  const isRight = sideLabel === 'Right Side';
  const sideWords = isRight ? '(?:right|r)' : '(?:left|l)';
  const oppositeSideWords = isRight ? '(?:left|l)' : '(?:right|r)';

  // Extract side-specific segment from mixed strings (e.g. "Left 20; Right 30", "L:20, R:30").
  const scopedPattern = new RegExp(
    `\\b${sideWords}\\b\\s*[:\\-]?\\s*([^;,\\n]+?)(?=\\s*\\b${oppositeSideWords}\\b|[;\\n,]|$)`,
    'i'
  );
  const scopedMatch = text.match(scopedPattern);
  if (scopedMatch && scopedMatch[1]) {
    return scopedMatch[1].trim();
  }

  return fallbackToRaw ? text : '';
}

function getProcedureQuestions(structure, state) {
  // Find all chart items in Procedures section
  const procedurePanels = document.querySelectorAll('mat-expansion-panel');

  procedurePanels.forEach((panel) => {
    const ariaLabel = panel.getAttribute('aria-label');

    // Only process Procedures section
    if (!ariaLabel || !ariaLabel.includes('Procedures')) {
      return;
    }

    const sectionLabel = ariaLabel;
    const chartItems = panel.querySelectorAll('fx-chart-item');

    chartItems.forEach((chartItem) => {
      // Get the procedure name from aria-label or chart-item-label
      const procedureNameElement = chartItem.querySelector('.chart-item-label');
      const procedureName = procedureNameElement
        ? procedureNameElement.textContent.trim()
        : chartItem.getAttribute('aria-label') || 'Unknown Procedure';

      // Ensure section exists
      if (!structure[sectionLabel]) {
        structure[sectionLabel] = {
          Question: sectionLabel,
          Structure: {},
          OriginalElement: chartItem
        };
      }

      // Use procedure name as base label
      const baseLabel = procedureName;
      if (!structure[sectionLabel].Structure[baseLabel]) {
        structure[sectionLabel].Structure[baseLabel] = {};
      }

      // Extract Minutes (quantity field)
      const minutesInput = chartItem.querySelector(
        'clnt-input-number[clntformcontrolname="quantity"] input[digitonly], input[clntformcontrolname="quantity"][digitonly]'
      ) as HTMLInputElement;
      if (minutesInput) {
        const minutesKey = `${sectionLabel} - ${baseLabel} - Minutes`;
        structure[sectionLabel].Structure[baseLabel][minutesKey] = {
          'Existing Value': minutesInput.value || '',
          Description: 'IMPORTANT: All fields in this procedure row (Minutes, Measure, S, R, KG, etc.) share a combined 50-byte limit. Keep this value extremely brief — a number or very short abbreviation only.',
          Input: async (val: string, replyType: string) => {
            if (isEmptyOrNull(val)) {
              return;
            }
            minutesInput.focus();
            minutesInput.click();
            if (replyType === 'append') {
              minutesInput.value = (minutesInput.value + '\n' + val).trim();
            } else {
              minutesInput.value = val;
            }
            ['input', 'change', 'blur'].forEach((event) =>
              minutesInput.dispatchEvent(new Event(event, { bubbles: true }))
            );
          }
        };
        addCaptureSafeInputListener(state, minutesInput, function () {
          structure[sectionLabel].Structure[baseLabel][minutesKey][
            'Existing Value'
          ] = minutesInput.value;
        });
      }

      // Extract sets, reps, sec (value fields with labels)
      const valueInputs = chartItem.querySelectorAll(
        'clnt-input-text[clntformcontrolname="value"] input[type="text"], input[clntformcontrolname="value"][type="text"]'
      );
      valueInputs.forEach((valueInput) => {
        // Find the label for this input (the label element before the input)
        const labelAndField = valueInput.closest('.label-and-field');
        const labelElement = labelAndField
          ? labelAndField.querySelector('label')
          : null;
        const fieldLabel = labelElement
          ? labelElement.textContent.trim()
          : 'value';

        // Skip empty labels
        if (!fieldLabel || fieldLabel === '') {
          return;
        }

        const fieldKey = `${sectionLabel} - ${baseLabel} - ${fieldLabel}`;
        const inputElement = valueInput as HTMLInputElement;

        structure[sectionLabel].Structure[baseLabel][fieldKey] = {
          'Existing Value': inputElement.value || '',
          Description: 'IMPORTANT: All fields in this procedure row (Minutes, Measure, S, R, KG, etc.) share a combined 50-byte limit. Keep this value extremely brief — a number or very short abbreviation only.',
          Input: async (val: string, replyType: string) => {
            if (isEmptyOrNull(val)) {
              return;
            }
            inputElement.focus();
            inputElement.click();
            if (replyType === 'append') {
              inputElement.value = (inputElement.value + '\n' + val).trim();
            } else {
              inputElement.value = val;
            }
            ['input', 'change', 'blur'].forEach((event) =>
              inputElement.dispatchEvent(new Event(event, { bubbles: true }))
            );
          }
        };
        addCaptureSafeInputListener(state, inputElement, function () {
          structure[sectionLabel].Structure[baseLabel][fieldKey][
            'Existing Value'
          ] = inputElement.value;
        });
      });

      // Extract Notes
      const notesTextarea = chartItem.querySelector(
        'clnt-input-textarea[clntformcontrolname="note"] textarea, textarea[clntformcontrolname="note"]'
      ) as HTMLTextAreaElement;
      if (notesTextarea) {
        const notesKey = `${sectionLabel} - ${baseLabel} - Notes`;
        structure[sectionLabel].Structure[baseLabel][notesKey] = {
          'Existing Value': notesTextarea.value || '',
          Input: async (val: string, replyType: string) => {
            if (isEmptyOrNull(val)) {
              return;
            }
            notesTextarea.focus();
            notesTextarea.click();
            if (replyType === 'append') {
              notesTextarea.value = (notesTextarea.value + '\n' + val).trim();
            } else {
              notesTextarea.value = val;
            }
            ['input', 'change', 'blur'].forEach((event) =>
              notesTextarea.dispatchEvent(new Event(event, { bubbles: true }))
            );
          }
        };
        addCaptureSafeInputListener(state, notesTextarea, function () {
          structure[sectionLabel].Structure[baseLabel][notesKey][
            'Existing Value'
          ] = notesTextarea.value;
        });
      }
    });
  });

  return structure;
}

function addCaptureSafeInputListener(state, element, listener) {
  if (!state.disableScrapeSideEffects) {
    element.addEventListener('input', listener);
  }
}

function getClosestSectionLabel(element) {
  let section = element.closest('fx-visit-area, mat-expansion-panel');
  if (section) {
    let header = section.querySelector('mat-panel-title, h4');
    return header ? header.textContent.trim() : 'Unknown Section';
  }
  return 'Unknown Section';
}

function getClosestLabel(element) {
  let label = 'Unknown Field';

  // Prefer group-level header (e.g. "History of Present Condition") from chart-item wrappers:
  // look at previous sibling or parent's first child H4.
  const chartWrapper = element.closest(
    '.chart-item-action-wrapper, .chart-item-row, .chart-item-container'
  );
  if (chartWrapper) {
    const prevSibling = chartWrapper.previousElementSibling;
    if (prevSibling?.tagName === 'H4' && prevSibling.textContent?.trim()) {
      label = prevSibling.textContent.trim();
    }
    if (label === 'Unknown Field' && chartWrapper.parentElement) {
      const firstChild = chartWrapper.parentElement.firstElementChild;
      if (firstChild?.tagName === 'H4' && firstChild.textContent?.trim()) {
        label = firstChild.textContent.trim();
      }
    }
  }

  if (label === 'Unknown Field') {
    let fieldWrapper = element.closest(
      'fieldset, mat-form-field, .chart-item-action-wrapper'
    );
    if (fieldWrapper) {
      let foundLabel = fieldWrapper.querySelector(
        'mat-label, span.chart-item-label, h4'
      );
      if (foundLabel && foundLabel.textContent?.trim()) {
        label = foundLabel.textContent.trim();
      }
    }
  }

  if (label === 'Unknown Field') {
    let previousLabel = element.closest(
      '.chart-item-container'
    )?.previousElementSibling;
    if (previousLabel && previousLabel.tagName === 'H4') {
      label = previousLabel.textContent.trim();
    }
  }

  if (label === 'Unknown Field') {
    if (element.getAttribute('aria-label')) {
      label = element.getAttribute('aria-label').trim();
    } else if (element.id) {
      label = element.id.trim();
    }
  }

  return label;
}

function getSubBaseLabel(element) {
  let subLabel = 'Unknown Sub-Label';
  let currentElement = element;

  // Traverse multiple levels up to find a valid sub-label
  while (currentElement && currentElement !== document.body) {
    let subLabelWrapper = currentElement.closest(
      '.chart-item-action-wrapper, .chart-item-row, .chart-item-container'
    );

    if (subLabelWrapper) {
      let foundLabel = subLabelWrapper.querySelector(
        'span.chart-item-label, h4'
      );
      if (foundLabel) {
        subLabel = foundLabel.textContent.trim();
        break; // Stop at the first valid sub-label
      }
    }
    currentElement = currentElement.parentElement; // Move one level up
  }

  return subLabel !== 'Unknown Sub-Label' ? subLabel : null;
}

function isEmptyOrNull(value) {
  return (
    value === null || value === undefined || value === '' || value === 'None'
  );
}

export function answerQuestions(answers) {
  /*
    Description: This function will take the answers and populate the questions
    
    Args: 
        answers: A dictionary of answers
            "Question": "The question in question"
            "Answer": "The answer to the question"
   
    Return: A list of any questions that were not answered, perhaps the answer didn't match the questions expected or whatnot...
        [
            {
                Question: "Where was the injury?", 
                Answer: "Back" 
                Error: "The error message" (Choice not found, etc)
            },
            ...
         }

    */
  var errors = [];
  answers.forEach((answer) => {
    var error = answerQuestion(
      answer['Question'],
      answer['Answer'],
      answer['Reply Type']
    );
    if (error) {
      errors.push({
        Question: answer['Question'],
        Answer: answer['Answer'],
        Error: error
      });
    }
  });
  console.error('Errors:');
  console.error(errors);
  return errors;
}

export async function answerQuestion(question, answer, replyType) {
  /*
    Description: This function will take the answer and populate the question
    
    Args: 
        question: The question in question
        answer: The answer to the question
   
    Return: null if success otherwise a string with the error message
    */

  // console.log("Answering Question: '" + question + "' with '" + answer+"'")

  // Make sure the question exists
  if (questionInteractions[question] == null) {
    console.error('Question not found');
    console.error(question);
    // console.log(questionInteractions)
    // console.log(Object.keys(questionInteractions))

    return 'Question not found';
  }

  // If it's a flex do so
  if (questionInteractions[question]['Type'] === 'Flex') {
    return await answerFlexQuestion(question, answer, replyType);
  }

  // If it is a Long Answer or long answer, just populate the input
  if (questionInteractions[question]['Input']) {
    questionInteractions[question]['Input'](answer, replyType);
    return true;
  }

  // Check if the answer is a list of answers or just a single answer
  if (Array.isArray(answer)) {
    // Choose All
    var notFoundAnswers = [];
    for (var i = 0; i < answer.length; i++) {
      if (answer[i] in questionInteractions[question]['Choices']) {
        console.log('Answer found ' + answer[i]);
        questionInteractions[question]['Choices'][answer[i]](true);
        // sleep(1000).then(() => {
        //     questionInteractions[question][answer[i]]()
        // });
      } else {
        notFoundAnswers.push(answer[i]);
      }
    }
    if (notFoundAnswers.length > 0) {
      console.error('The following answers were not found: ' + notFoundAnswers);
      return 'The following answers were not found: ' + notFoundAnswers;
    } else {
      return true;
    }
  } else {
    // Multiple Choice
    if (answer in questionInteractions[question]['Choices']) {
      questionInteractions[question]['Choices'][answer]();
      return true;
    } else {
      console.error('Answer not found');
      console.log('Question: ' + question);
      console.log('Answer:' + answer);
      console.log(questionInteractions[question]['Choices']);
      return 'Answer not found';
    }
  }
}

async function answerFlexQuestion(question, answer, replyType = 'overwrite') {
  try {
    const errors = [];
    const questionObject = questionInteractions[question];

    if (!questionObject) {
      console.error(`Question with Key "${question}" not found.`);
      return {
        Question: question,
        Error: `Question with Key "${question}" not found.`
      };
    }

    console.log('Question found:', questionObject);

    const structure = questionObject.Structure || {};

    /** Do the answering sequentially */
    for (const [fieldKey, fieldDetails] of Object.entries(answer)) {
      const field = structure[fieldKey];

      if (!field) {
        console.error(`Field "${fieldKey}" not found in the Structure.`);
        errors.push({ Field: fieldKey, Error: 'Field not found in Structure' });
        continue;
      }

      for (const [subFieldKey, value] of Object.entries(fieldDetails)) {
        if (field[subFieldKey]?.Input) {
          try {
            await field[subFieldKey].Input(value, replyType);
          } catch (error) {
            errors.push({
              Field: `${fieldKey}.${subFieldKey}`,
              Error: `Error applying value: ${error.message}`
            });
          }
        } else {
          console.warn(
            `Input function not found for "${fieldKey}.${subFieldKey}"`
          );
          errors.push({
            Field: `${fieldKey}.${subFieldKey}`,
            Error: 'Input function not found'
          });
        }
      }
    }

    /** Parse the errors */
    if (errors.length > 0) {
      console.error('Errors occurred:', errors);
      return errors;
    }

    return null; // Success
  } catch (error) {
    console.error('Unexpected error:', error);
    return {
      Question: question,
      Error: `Unexpected error: ${error.message}`
    };
  }
}

export async function getLogFromInjector() {
  try {
    const htmlContent = document.documentElement.outerHTML;
    chrome.runtime.sendMessage({
      type: 'INJECTOR_ERROR',
      error: { htmlContent: htmlContent }
    });
    return { htmlContent: htmlContent };
  } catch (error) {
    // Return the full HTML content of the document
    console.error('Error occurred while fetching document:', error);
    getLogFromInjector();
  }
}

// ---------------------------------------------------------------------------
// Compliance badge — overlays a grade indicator on the Clinicient sign-off
// pen button and gates B/C/F grades with a documentation-gaps popup.
// ---------------------------------------------------------------------------

const COMP_SIGN_OFF_SELECTOR = '[data-testid="sign-off-button"]';
const COMP_BADGE_ID = 'comp-signoff-badge';
const COMP_POPUP_ID = 'comp-signoff-popup';
const COMP_TOOLTIP_ID = 'comp-signoff-tooltip';

export function removeComplianceBadge(): void {
  [COMP_BADGE_ID, COMP_POPUP_ID, COMP_TOOLTIP_ID, 'comp-signoff-backdrop'].forEach((id) => {
    const el = document.getElementById(id);
    if (el) el.remove();
  });
  const btn = document.querySelector(COMP_SIGN_OFF_SELECTOR) as HTMLElement | null;
  if (btn && (btn as any).__compClickHandler__) {
    btn.removeEventListener('click', (btn as any).__compClickHandler__, true);
    delete (btn as any).__compClickHandler__;
  }
  if ((window as any).__compSignOffRepos__) {
    (window as any).__compSignOffRepos__();
    delete (window as any).__compSignOffRepos__;
  }
}

export function injectComplianceBadge(grade: string | null, reasons: any[], autoShow = false, signOffGated = false): void {
  removeComplianceBadge();
  if (!grade || !signOffGated) return;

  const button = document.querySelector(COMP_SIGN_OFF_SELECTOR) as HTMLElement | null;
  if (!button) return;

  const color = compGradeColor(grade);

  // Fixed-position container mirroring the button's bounding rect
  const badge = document.createElement('div');
  badge.id = COMP_BADGE_ID;
  badge.style.cssText =
    'position:fixed;z-index:2147483646;pointer-events:none;box-sizing:border-box;';

  const ring = document.createElement('div');
  ring.style.cssText =
    `position:absolute;inset:0;border:2.5px solid ${color};` +
    `border-radius:6px;box-sizing:border-box;`;

  const dot = document.createElement('div');
  dot.style.cssText =
    `position:absolute;top:-9px;right:-9px;width:20px;height:20px;` +
    `background:${color};border-radius:50%;border:2px solid white;` +
    `box-shadow:0 1px 3px rgba(0,0,0,.3);display:flex;align-items:center;` +
    `justify-content:center;font-size:11px;font-weight:700;color:white;` +
    `font-family:system-ui,sans-serif;pointer-events:auto;cursor:default;`;

  if (grade === 'A') {
    dot.innerHTML =
      '<svg width="12" height="12" viewBox="0 0 12 12" fill="none">' +
      '<path d="M2 6l3 3 5-5" stroke="white" stroke-width="2"' +
      ' stroke-linecap="round" stroke-linejoin="round"/></svg>';
  } else {
    dot.textContent = grade;
  }

  badge.appendChild(ring);
  badge.appendChild(dot);

  const positionBadge = (): void => {
    const r = button!.getBoundingClientRect();
    if (!r.width) return;
    badge.style.left = r.left + 'px';
    badge.style.top = r.top + 'px';
    badge.style.width = r.width + 'px';
    badge.style.height = r.height + 'px';
  };

  positionBadge();
  document.body.appendChild(badge);

  // Backdrop + modal wrapper
  const backdrop = document.createElement('div');
  backdrop.id = 'comp-signoff-backdrop';
  backdrop.style.cssText =
    'position:fixed;inset:0;z-index:2147483646;background:rgba(0,0,0,0.45);display:none;' +
    'align-items:center;justify-content:center;';

  const popup = document.createElement('div');
  popup.id = COMP_POPUP_ID;
  popup.style.cssText =
    'background:white;border-radius:12px;' +
    'box-shadow:0 8px 32px rgba(0,0,0,.25);padding:20px;width:500px;max-width:90vw;' +
    'font-family:system-ui,-apple-system,sans-serif;max-height:85vh;overflow-y:auto;';

  backdrop.appendChild(popup);


  if (grade === 'A') {
    // Grade A: tooltip card positioned near the badge, no backdrop
    const tooltip = document.createElement('div');
    tooltip.id = COMP_TOOLTIP_ID;
    tooltip.style.cssText =
      'position:fixed;z-index:2147483647;background:white;border-radius:10px;' +
      'box-shadow:0 4px 20px rgba(0,0,0,.15);padding:16px;width:280px;' +
      'font-family:system-ui,-apple-system,sans-serif;display:none;';
    tooltip.innerHTML =
      `<div style="display:flex;align-items:center;gap:10px;margin-bottom:10px;">` +
      `<div style="width:34px;height:34px;border-radius:50%;background:${color};display:flex;` +
      `align-items:center;justify-content:center;flex-shrink:0;">` +
      `<svg width="16" height="16" viewBox="0 0 16 16" fill="none">` +
      `<path d="M3 8l3.5 3.5 6.5-6.5" stroke="white" stroke-width="2"` +
      ` stroke-linecap="round" stroke-linejoin="round"/></svg></div>` +
      `<p style="font-size:14px;font-weight:700;margin:0;color:${color};">Compliant — Grade A</p>` +
      `</div>` +
      `<p style="font-size:13px;color:#374151;margin:0 0 10px 0;line-height:1.5;">` +
      `This note meets CMS documentation guidelines. Click the pen to sign off.</p>` +
      `<p style="font-size:11px;color:#9ca3af;margin:0;text-align:center;">Powered by Comprehend PT</p>`;
    document.body.appendChild(tooltip);

    const positionTooltip = (): void => {
      const r = badge.getBoundingClientRect();
      let left = r.left;
      let top = r.bottom + 8;
      if (left + 280 > window.innerWidth) left = Math.max(8, window.innerWidth - 288);
      if (top + 160 > window.innerHeight) top = r.top - 168;
      tooltip.style.left = left + 'px';
      tooltip.style.top = top + 'px';
    };

    // Only the corner dot takes clicks. The badge mirrors the pen's full
    // bounding rect, so pointer-events on it swallowed the sign-off click.
    dot.style.cursor = 'pointer';
    dot.addEventListener('click', (e: MouseEvent) => {
      e.stopPropagation();
      if (tooltip.style.display === 'none') {
        positionTooltip();
        tooltip.style.display = 'block';
      } else {
        tooltip.style.display = 'none';
      }
    });

    document.addEventListener('click', () => { tooltip.style.display = 'none'; });
  } else {
    // Same popup the free-tier score bubble shows -- built by the shared
    // module -- with Clinicient's own sign-off actions appended.
    popup.innerHTML = buildComplianceModalHTML(grade, reasons, {
      guidance:
        'Record more information or edit the note in your EMR to raise the grade, ' +
        'then click the pen again to re-check &mdash; or sign off anyway.',
      actionsHtml:
        `<div style="display:flex;gap:8px;margin-bottom:16px;">` +
        `<button id="comp-back-btn" style="flex:1;padding:11px;border-radius:8px;` +
        `border:none;background:${color};font-size:13px;font-weight:600;` +
        `color:white;cursor:pointer;">Back to note</button>` +
        `<button id="comp-signoff-anyway-btn" style="flex:1;padding:11px;border-radius:8px;` +
        `border:1.5px solid #d1d5db;background:white;font-size:13px;font-weight:600;color:#374151;` +
        `cursor:pointer;">Sign off anyway</button>` +
        `</div>`
    });
  }

  popup.addEventListener('click', (e: MouseEvent) => {
    const t = e.target as HTMLElement;
    if (t.id === 'comp-close-btn' || t.closest('#comp-close-btn') ||
        t.id === 'comp-back-btn' || t.closest('#comp-back-btn')) {
      backdrop.style.display = 'none';
    } else if (t.id === 'comp-signoff-anyway-btn' || t.closest('#comp-signoff-anyway-btn')) {
      removeComplianceBadge(); // removes our interceptor — Clinicient for now
      if (button) button.click(); // complete the sign-off the user already intended
    }
  });

  // Capture-phase — runs before Angular's bubble-phase listener
  const clickHandler = (e: Event): void => {
    e.preventDefault();
    e.stopImmediatePropagation();
    // Show loading state while score is re-fetched
    popup.innerHTML = buildComplianceLoadingHTML(
      'Re-checking compliance...',
      'Re-grading the note against CMS documentation criteria'
    );
    ensureComplianceSpinnerStyle();
    const loadingCloseBtn = popup.querySelector('#comp-loading-close-btn');
    if (loadingCloseBtn) {
      loadingCloseBtn.addEventListener('click', () => {
        backdrop.style.display = 'none';
      });
    }
    backdrop.style.display = 'flex';
    // Trigger compliance score refresh in the React app
    try {
      if (typeof chrome !== 'undefined' && chrome.runtime && chrome.runtime.sendMessage) {
        chrome.runtime.sendMessage({ type: 'COMPLIANCE_PEN_CLICKED' }).catch(() => {});
      }
    } catch (_) {}
    try {
      if ((window as any).ComprehendEmbed) {
        (window as any).ComprehendEmbed.send('COMPLIANCE_PEN_CLICKED', {});
      }
    } catch (_) {}
  };
  if (signOffGated) {
    (button as any).__compClickHandler__ = clickHandler;
    button.addEventListener('click', clickHandler, true);
  }

  // Click backdrop outside modal to close
  backdrop.addEventListener('click', (e: MouseEvent) => {
    if (e.target === backdrop) backdrop.style.display = 'none';
  });

  document.body.appendChild(backdrop);

  if (autoShow) {
    if (grade === 'A') {
      // COM-427: the pen re-grades on every click, grade A included, and the
      // result is always shown -- never sign a note on a grade the clinician
      // has not seen.
      popup.innerHTML = buildComplianceModalHTML(grade, reasons, {
        guidance:
          'This note meets CMS documentation guidelines. Sign off to finish, ' +
          'or go back to the note.',
        actionsHtml:
          `<div style="display:flex;gap:8px;margin-bottom:16px;">` +
          `<button id="comp-signoff-anyway-btn" style="flex:1;padding:11px;border-radius:8px;` +
          `border:none;background:${color};font-size:13px;font-weight:600;` +
          `color:white;cursor:pointer;">Sign off</button>` +
          `<button id="comp-back-btn" style="flex:1;padding:11px;border-radius:8px;` +
          `border:1.5px solid #d1d5db;background:white;font-size:13px;font-weight:600;color:#374151;` +
          `cursor:pointer;">Back to note</button>` +
          `</div>`
      });
    }
    backdrop.style.display = 'flex';
  }

  const reposHandler = (): void => {
    positionBadge();
  };
  window.addEventListener('resize', reposHandler);
  window.addEventListener('scroll', reposHandler, true);
  (window as any).__compSignOffRepos__ = (): void => {
    window.removeEventListener('resize', reposHandler);
    window.removeEventListener('scroll', reposHandler, true);
  };

}
